import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../../firebase';
import { useAuth } from '../../../hooks/useAuth';
import { getOrgConfig } from '../../utils/orgResolution';
import { apiListTeacherTasks, apiAcceptTeacherStatement } from '../../utils/rmsApi';
import { groupTeacherPupils, teacherPupilSummary } from '../../utils/teacherPupils';
import HarbourTopBar from '../../components/harbour/HarbourTopBar';
import HarbourSchoolStaffStatement from '../../components/harbour/HarbourSchoolStaffStatement';
import HarbourStatusChip from '../../components/harbour/HarbourStatusChip';
import HarbourButton from '../../components/harbour/HarbourButton';
import HarbourNotice from '../../components/harbour/HarbourNotice';
import HarbourTextLink from '../../components/harbour/HarbourTextLink';
import HarbourPathwayMark from '../../components/harbour/HarbourPathwayMark';
import { HarbourGlyph } from '../../components/harbour/HarbourIcon';
import '../../components/harbour/harbour.css';

/*
 * The teacher's "Your pupils" (TDI-620; Tom's ruling T-12): one row per
 * pupil they have forms for, with the year group, the school (only when
 * they are on more than one school's list), the pathway, "{x} of {y} forms
 * done", a status chip and "Open". Pupils whose forms are all finished sit
 * under "Done" at the bottom. "Open" goes to that pupil's forms page.
 *
 * No Figma frame exists for this list: it is built from the SENCO's "Your
 * pupils" rows and status chips, and Diana adds it to the design after.
 * Everything comes from rmsListTeacherTasks — a teacher has no client reads.
 * The school-staff statement (TDI-358) still comes first, once.
 */

function PupilRow({ child, multipleSchools, onOpen }) {
  const summary = teacherPupilSummary(child);
  const glyph = summary.allDone ? 'ring-tick' : summary.done > 0 ? 'ring-half' : 'ring-empty';
  const facts = [multipleSchools ? child.schoolName : null].filter(Boolean);
  return (
    <li className="harbour-teacher-pupil">
      <div className="harbour-teacher-pupil__facts">
        <span className="flex flex-wrap items-baseline gap-x-2">
          <span className="harbour-body-16-bold">{child.pupilName}</span>
          {child.yearGroup && (
            <span className="harbour-body-16 text-[color:var(--harbour-colour-text-muted)]">{child.yearGroup}</span>
          )}
        </span>
        <span className="flex flex-wrap items-center gap-x-4 gap-y-1 harbour-body-16">
          {facts.map((f) => (
            <span key={f} className="text-[color:var(--harbour-colour-text-muted)]">{f}</span>
          ))}
          {summary.pathways.map((p, i) => (
            <span key={p} className="inline-flex items-center gap-2">
              <HarbourPathwayMark pathway={p} />
              {summary.pathwayLabels[i]}
            </span>
          ))}
          <span className="inline-flex items-center gap-2">
            <HarbourGlyph kind={glyph} size={20} />
            {`${summary.done} of ${summary.total} forms done`}
          </span>
        </span>
      </div>
      <span className="harbour-teacher-pupil__actions">
        <HarbourStatusChip
          status={summary.chip}
          tone={summary.chip === 'done' ? (summary.pathways.includes('sen') ? 'sen' : 'referral') : 'neutral'}
        />
        {/* Secondary on every row: one filled button per view, and a list
            of pupils has no single main action. */}
        <HarbourButton kind="secondary" aria-label={`Open ${child.pupilName}`} onClick={onOpen}>
          Open
        </HarbourButton>
      </span>
    </li>
  );
}

export default function TeacherHomePage() {
  const navigate = useNavigate();
  const { user, claims } = useAuth();
  const orgId = claims?.rmsOrgId || getOrgConfig().orgId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tasks, setTasks] = useState(null);
  const [accepting, setAccepting] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await apiListTeacherTasks({ orgId });
      setTasks(res);
      // The first call links the account and sets the RMS Teacher claims:
      // refresh the token so routing sees them (the dashboard bootstrap
      // pattern).
      if (!claims?.userType) {
        try { await auth.currentUser?.getIdToken(true); } catch (_) { /* no-op */ }
      }
    } catch (e) {
      setError(e?.message || 'Your pupils could not be loaded. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [orgId, claims?.userType]);

  useEffect(() => { load(); }, [load]);

  const accept = async () => {
    setAccepting(true);
    setError(null);
    try {
      await apiAcceptTeacherStatement({ orgId });
      setTasks((t) => ({ ...t, statementAccepted: true }));
    } catch (e) {
      setError(e?.message || 'That did not work. Check your connection and try again.');
    } finally {
      setAccepting(false);
    }
  };

  const userName = [tasks?.firstName].filter(Boolean).join(' ') || undefined;
  // Teachers on a password (TDI-619) can change it from the menu (TDI-702).
  const teacherHasPassword = claims?.authMode === 'password';
  const children = (tasks && tasks.children) || [];
  const { open, done } = groupTeacherPupils(children);
  const hasPupils = open.length + done.length > 0;
  const openPupil = (child) => navigate(`/rms/teacher/pupils/${encodeURIComponent(child.patientRecordId)}`);

  return (
    <div className="harbour harbour-page">
      <div className="mx-auto max-w-[1080px] px-4 sm:px-6 xl:px-10 py-5 flex flex-col gap-6 text-left">
        <HarbourTopBar
          navItems={[]}
          userName={userName}
          accountEmail={user?.email}
          showChangePassword={teacherHasPassword}
          onSignOut={async () => {
            try { await auth.signOut(); } catch (_) { /* no-op */ }
            // Teachers sign in with a password (TDI-619).
            navigate('/rms/school-sign-in?signedout=1');
          }}
        />

        {loading && <p className="harbour-body-16">Loading your pupils…</p>}
        {!loading && error && (
          <HarbourNotice kind="error" title="That did not work">
            {error}
            {' '}
            <HarbourTextLink onClick={load}>Try again</HarbourTextLink>
          </HarbourNotice>
        )}

        {/* The statement school staff accept before first use (TDI-358). It
            is only recorded once something is given, so with nothing given
            the page goes straight to the empty list. */}
        {!loading && !error && tasks && !tasks.statementAccepted && hasPupils && (
          <div className="harbour-form-card text-left">
            <h1 className="harbour-title-20">Before you start</h1>
            <HarbourSchoolStaffStatement />
            <div className="harbour-form-footer">
              <HarbourButton kind="primary" busy={accepting} onClick={accept}>
                I understand, show my forms
              </HarbourButton>
            </div>
          </div>
        )}

        {!loading && !error && tasks && (tasks.statementAccepted || !hasPupils) && (
          <>
            <div className="flex flex-col gap-1 text-left">
              <h1 className="harbour-heading-32">Your pupils</h1>
              {/* The standfirst only holds when there is someone to help
                  with; the empty state says the rest. */}
              {hasPupils && (
                <p className="harbour-body-16 text-[color:var(--harbour-colour-text-muted)]">
                  These are the pupils your SENCO has asked you to help with. Open a pupil to see your forms. Your answers save as you go.
                </p>
              )}
            </div>
            {!hasPupils && (
              <div className="harbour-form-card text-left">
                <p className="harbour-body-16">
                  Nothing is waiting for you right now. When a SENCO gives you forms to fill in, they appear here.
                </p>
              </div>
            )}
            {open.length > 0 && (
              <ul className="harbour-card rounded-3xl px-4 sm:px-6" aria-label="Your pupils">
                {open.map((child) => (
                  <PupilRow
                    key={child.patientRecordId}
                    child={child}
                    multipleSchools={Boolean(tasks.multipleSchools)}
                    onOpen={() => openPupil(child)}
                  />
                ))}
              </ul>
            )}
            {done.length > 0 && (
              <section aria-labelledby="teacher-pupils-done" className="flex flex-col gap-3">
                <h2 id="teacher-pupils-done" className="harbour-title-20">Done</h2>
                <ul className="harbour-card rounded-3xl px-4 sm:px-6" aria-label="Done">
                  {done.map((child) => (
                    <PupilRow
                      key={child.patientRecordId}
                      child={child}
                      multipleSchools={Boolean(tasks.multipleSchools)}
                      onOpen={() => openPupil(child)}
                    />
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
